/**
 * Lookup araması — web'deki `SearchableSelect` ile aynı sıralama.
 *
 * Tasarımcıdaki seçim kutuları iki bin beş yüzü aşan nesne listesi taşıyabiliyor;
 * tarayıcının kendi `<select>` kutusunda bunu bulmanın tek yolu listeyi gezmekti.
 *
 * Sıralama kuralı web'den birebir alındı: tam eşleşme → baştan eşleşme → kelime başı →
 * içeride geçen → çok kelimeli eşleşme. Aynı sırada olanlar arasında eşleşmenin konumu,
 * sonra kısa metin, sonra alfabetik sıra belirleyici.
 */

export interface SearchOption {
  id: string;
  name: string;
}

/**
 * Türkçe karakterleri sadeleştirir.
 *
 * "ürün" yazan biri "urun" ile de bulabilmeli; `ı/İ/I/i` hepsi `i`'ye iner, böylece
 * Türkçe'nin noktasız i sorunu aramayı bölmez.
 */
export function normalizeSearch(text: string | null | undefined): string {
  if (!text) return '';
  let out = '';
  for (const ch of text) {
    switch (ch) {
      case 'ı':
      case 'İ':
      case 'I':
      case 'i':
        out += 'i';
        break;
      case 'ş':
      case 'Ş':
        out += 's';
        break;
      case 'ğ':
      case 'Ğ':
        out += 'g';
        break;
      case 'ü':
      case 'Ü':
        out += 'u';
        break;
      case 'ö':
      case 'Ö':
        out += 'o';
        break;
      case 'ç':
      case 'Ç':
        out += 'c';
        break;
      default:
        out += ch.toLowerCase();
    }
  }
  return out;
}

/** Eşleşmenin kalitesi: küçük `rank` daha iyi. `rank === null` → eşleşme yok. */
export function scoreMatch(
  normText: string,
  query: string,
  tokens: string[],
): { rank: number; pos: number } | null {
  // Çok kelimeli arama AND mantığı: her parça metinde geçmeli.
  for (const token of tokens) {
    if (!normText.includes(token)) return null;
  }

  if (normText === query) return { rank: 0, pos: 0 };
  if (normText.startsWith(query)) return { rank: 1, pos: 0 };

  const index = normText.indexOf(query);
  if (index > 0) {
    const wordStart = normText[index - 1] === ' ' || normText[index - 1] === '_';
    return { rank: wordStart ? 2 : 3, pos: index };
  }

  // Sorgu tek parça geçmiyor ama tüm parçalar var.
  return { rank: 4, pos: tokens.length > 0 ? normText.indexOf(tokens[0]!) : 0 };
}

/** Arama metnine göre süzülmüş ve sıralanmış seçenekler. Boş sorguda liste olduğu gibi. */
export function filterOptions<T extends SearchOption>(options: T[], search: string): T[] {
  const query = normalizeSearch(search).trim();
  if (!query) return options;
  const tokens = query.split(' ').filter(Boolean);

  return options
    .map((option) => {
      const score = scoreMatch(normalizeSearch(option.name), query, tokens);
      return score ? { option, ...score } : null;
    })
    .filter((x): x is { option: T; rank: number; pos: number } => x !== null)
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        a.pos - b.pos ||
        a.option.name.length - b.option.name.length ||
        a.option.name.localeCompare(b.option.name, 'tr'),
    )
    .map((x) => x.option);
}

/**
 * Bir seferde çizilen en fazla satır (web ile aynı).
 *
 * Tasarımcıda iki binden fazla nesne var; hepsini çizmek açılışı bekletiyordu. Kalanlar
 * için kullanıcıya "yazmaya devam edin" deniyor.
 */
export const MAX_OPTIONS = 200;
